import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { 
  Search, Plus, MessageSquare, Clock, MapPin, DollarSign, 
  Loader2, Filter, Building2, User, Send, AlertCircle, Zap,
  Package, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token')}`,
  'Content-Type': 'application/json'
});

const CommunityBlogPage = () => {
  const { user, isAuthenticated } = useAuth();
  const { isUSA } = useLocale();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialog, setCreateDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, post: null, responses: [] });
  const [responseDialog, setResponseDialog] = useState({ open: false, post: null });
  const [processing, setProcessing] = useState(false);
  
  // New post form
  const [newPost, setNewPost] = useState({
    title: '',
    description: '',
    category: '',
    quantity_needed: '',
    location_preference: '',
    budget_range: '',
    urgency: 'normal'
  });
  
  // Response form
  const [responseForm, setResponseForm] = useState({
    message: '',
    price_offer: '',
    delivery_time: ''
  });

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        const [postsRes, categoriesRes] = await Promise.all([
          fetch(`${API_URL}/api/blog/posts${selectedCategory ? `?category=${selectedCategory}` : ''}`),
          fetch(`${API_URL}/api/blog/categories`)
        ]);
        
        if (isMounted && postsRes.ok) {
          const data = await postsRes.json();
          setPosts(data.posts || []);
        }
        if (isMounted && categoriesRes.ok) setCategories(await categoriesRes.json());
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    loadData();
    
    return () => { isMounted = false; };
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      const [postsRes, catsRes] = await Promise.all([
        fetch(`${API_URL}/api/blog/posts${selectedCategory ? `?category=${selectedCategory}` : ''}`),
        fetch(`${API_URL}/api/blog/categories`)
      ]);
      
      if (postsRes.ok) {
        const data = await postsRes.json();
        setPosts(data.posts || []);
      }
      if (catsRes.ok) {
        setCategories(await catsRes.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.title || !newPost.description || !newPost.category) {
      toast.error(isUSA ? 'Complete the required fields' : 'Completa los campos requeridos');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/posts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newPost)
      });
      
      if (res.ok) {
        toast.success(isUSA ? 'Post created' : 'Publicación creada');
        setCreateDialog(false);
        setNewPost({ title: '', description: '', category: '', quantity_needed: '', location_preference: '', budget_range: '', urgency: 'normal' });
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || (isUSA ? 'Error creating post' : 'Error al crear'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error creating post' : 'Error al crear publicación');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewPost = async (post) => {
    try {
      const res = await fetch(`${API_URL}/api/blog/posts/${post.post_id}`);
      if (res.ok) {
        const data = await res.json();
        setDetailDialog({ open: true, post: data.post, responses: data.responses || [] });
      }
    } catch (error) {
      toast.error(isUSA ? 'Error loading' : 'Error al cargar');
    }
  };

  const handleRespond = async () => {
    if (!responseForm.message) {
      toast.error(isUSA ? 'Write a message' : 'Escribe un mensaje');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/posts/${responseDialog.post.post_id}/respond`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          message: responseForm.message,
          price_offer: responseForm.price_offer ? parseFloat(responseForm.price_offer) : null,
          delivery_time: responseForm.delivery_time || null
        })
      });
      
      if (res.ok) {
        toast.success(isUSA ? 'Response sent' : 'Respuesta enviada');
        setResponseDialog({ open: false, post: null });
        setResponseForm({ message: '', price_offer: '', delivery_time: '' });
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || (isUSA ? 'Error' : 'Error'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error' : 'Error');
    } finally {
      setProcessing(false);
    }
  };

  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case 'urgente':
        return <Badge className="bg-red-100 text-red-700">{isUSA ? 'Urgent' : 'Urgente'}</Badge>;
      case 'flexible':
        return <Badge className="bg-blue-100 text-blue-700">{isUSA ? 'Flexible' : 'Flexible'}</Badge>;
      default:
        return <Badge variant="outline">{isUSA ? 'Normal' : 'Normal'}</Badge>;
    }
  };

  const getCategoryLabel = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat?.label || catId;
  };

  const filteredPosts = posts.filter(post => 
    !searchQuery || 
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div data-testid="community-blog-page">
      <div className="page-header">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Search className="w-6 h-6" style={{ color: '#1E4C8E' }} />
              {isUSA ? 'Community Board' : 'Blog Comunitario'}
            </h1>
            <p className="page-subtitle">
              {isUSA ? 'Post what you need and let suppliers find you' : 'Publica lo que buscas y deja que los proveedores te encuentren'}
            </p>
          </div>
          {isAuthenticated && (
            <Button onClick={() => setCreateDialog(true)} style={{ backgroundColor: '#34B772' }}>
              <Plus className="w-4 h-4 mr-2" />
              {isUSA ? 'Post search' : 'Publicar búsqueda'}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder={isUSA ? 'Search posts...' : 'Buscar publicaciones...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={isUSA ? 'All categories' : 'Todas las categorías'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isUSA ? 'All categories' : 'Todas las categorías'}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Posts Grid */}
      {filteredPosts.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">{isUSA ? 'No posts' : 'No hay publicaciones'}</h3>
            <p className="text-slate-500 mb-4">
              {selectedCategory 
                ? (isUSA ? 'No searches in this category' : 'No hay búsquedas en esta categoría') 
                : (isUSA ? 'Be the first to post what you need' : 'Sé el primero en publicar lo que buscas')}
            </p>
            {isAuthenticated && (
              <Button onClick={() => setCreateDialog(true)} style={{ backgroundColor: '#34B772' }}>
                <Plus className="w-4 h-4 mr-2" />
                {isUSA ? 'Create post' : 'Crear publicación'}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPosts.map(post => (
            <Card key={post.post_id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleViewPost(post)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <Badge variant="outline" className="bg-slate-50">
                    {getCategoryLabel(post.category)}
                  </Badge>
                  {getUrgencyBadge(post.urgency)}
                </div>
                
                <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">{post.title}</h3>
                <p className="text-sm text-slate-600 mb-3 line-clamp-3">{post.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-3 text-xs text-slate-500">
                  {post.quantity_needed && (
                    <span className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {post.quantity_needed}
                    </span>
                  )}
                  {post.location_preference && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {post.location_preference}
                    </span>
                  )}
                  {post.budget_range && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      {post.budget_range}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {post.company_name ? (
                      <>
                        <Building2 className="w-3 h-3" />
                        <span>{post.company_name}</span>
                      </>
                    ) : (
                      <>
                        <User className="w-3 h-3" />
                        <span>{post.user_name}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      {post.responses_count}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Post Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isUSA ? 'Post Search' : 'Publicar Búsqueda'}</DialogTitle>
            <DialogDescription>{isUSA ? 'Describe what you are looking for so suppliers can contact you' : 'Describe lo que estás buscando para que los proveedores te contacten'}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isUSA ? 'Title *' : 'Título *'}</Label>
              <Input 
                placeholder={isUSA ? 'Ex: Looking for organic cotton fabric' : 'Ej: Busco tela de algodón orgánico'}
                value={newPost.title}
                onChange={(e) => setNewPost({...newPost, title: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{isUSA ? 'Category *' : 'Categoría *'}</Label>
              <Select value={newPost.category} onValueChange={(v) => setNewPost({...newPost, category: v})}>
                <SelectTrigger><SelectValue placeholder={isUSA ? 'Select category' : 'Selecciona categoría'} /></SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{isUSA ? 'Description *' : 'Descripción *'}</Label>
              <Textarea 
                placeholder={isUSA ? 'Describe in detail what you need: specifications, use, features...' : 'Describe con detalle lo que necesitas: especificaciones, uso, características...'}
                value={newPost.description}
                onChange={(e) => setNewPost({...newPost, description: e.target.value})}
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isUSA ? 'Approximate quantity' : 'Cantidad aproximada'}</Label>
                <Input 
                  placeholder={isUSA ? 'Ex: 500 meters' : 'Ej: 500 metros'}
                  value={newPost.quantity_needed}
                  onChange={(e) => setNewPost({...newPost, quantity_needed: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>{isUSA ? 'Preferred location' : 'Ubicación preferida'}</Label>
                <Input 
                  placeholder={isUSA ? 'Ex: Texas, California' : 'Ej: CDMX, Jalisco'}
                  value={newPost.location_preference}
                  onChange={(e) => setNewPost({...newPost, location_preference: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isUSA ? 'Budget' : 'Presupuesto'}</Label>
                <Input 
                  placeholder={isUSA ? 'Ex: $2-4 per yard' : 'Ej: $50-80 por metro'}
                  value={newPost.budget_range}
                  onChange={(e) => setNewPost({...newPost, budget_range: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>{isUSA ? 'Urgency' : 'Urgencia'}</Label>
                <Select value={newPost.urgency} onValueChange={(v) => setNewPost({...newPost, urgency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgente">{isUSA ? 'Urgent' : 'Urgente'}</SelectItem>
                    <SelectItem value="normal">{isUSA ? 'Normal' : 'Normal'}</SelectItem>
                    <SelectItem value="flexible">{isUSA ? 'Flexible' : 'Flexible'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>{isUSA ? 'Cancel' : 'Cancelar'}</Button>
            <Button onClick={handleCreatePost} disabled={processing} style={{ backgroundColor: '#34B772' }}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Publish' : 'Publicar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ open, post: null, responses: [] })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {detailDialog.post && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className="mb-2">{getCategoryLabel(detailDialog.post.category)}</Badge>
                    <DialogTitle className="text-xl">{detailDialog.post.title}</DialogTitle>
                  </div>
                  {getUrgencyBadge(detailDialog.post.urgency)}
                </div>
              </DialogHeader>
              
              <div className="py-4">
                <p className="text-slate-700 mb-4">{detailDialog.post.description}</p>
                
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg mb-4">
                  {detailDialog.post.quantity_needed && (
                    <div>
                      <p className="text-xs text-slate-500">{isUSA ? 'Quantity' : 'Cantidad'}</p>
                      <p className="font-medium">{detailDialog.post.quantity_needed}</p>
                    </div>
                  )}
                  {detailDialog.post.location_preference && (
                    <div>
                      <p className="text-xs text-slate-500">{isUSA ? 'Location' : 'Ubicación'}</p>
                      <p className="font-medium">{detailDialog.post.location_preference}</p>
                    </div>
                  )}
                  {detailDialog.post.budget_range && (
                    <div>
                      <p className="text-xs text-slate-500">{isUSA ? 'Budget' : 'Presupuesto'}</p>
                      <p className="font-medium">{detailDialog.post.budget_range}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-slate-500">{isUSA ? 'Posted by' : 'Publicado por'}</p>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{detailDialog.post.company_name || detailDialog.post.user_name}</p>
                      {detailDialog.post.company_id && (
                        <Link 
                          to={`/company/${detailDialog.post.company_id}`}
                          className="text-blue-600 hover:underline text-sm"
                          onClick={() => setDetailDialog({...detailDialog, open: false})}
                        >
                          {isUSA ? 'View profile' : 'Ver perfil'}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Contact Author Button */}
                {isAuthenticated && detailDialog.post.user_id !== user?.user_id && detailDialog.post.company_id && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{isUSA ? 'Want to contact directly?' : '¿Quieres contactar directamente?'}</p>
                      <p className="text-sm text-slate-600">{isUSA ? `Send a message to ${detailDialog.post.company_name || detailDialog.post.user_name}` : `Envía un mensaje a ${detailDialog.post.company_name || detailDialog.post.user_name}`}</p>
                    </div>
                    <Link to={`/dashboard/messages?contact=${detailDialog.post.company_id}`} onClick={() => setDetailDialog({...detailDialog, open: false})}>
                      <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        {isUSA ? 'Send message' : 'Enviar mensaje'}
                      </Button>
                    </Link>
                  </div>
                )}
                
                {/* Responses */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900">
                      {isUSA ? 'Responses' : 'Respuestas'} ({detailDialog.responses.length})
                    </h3>
                    {isAuthenticated && detailDialog.post.user_id !== user?.user_id && (
                      <Button size="sm" onClick={() => { setDetailDialog({...detailDialog, open: false}); setResponseDialog({ open: true, post: detailDialog.post }); }} style={{ backgroundColor: '#34B772' }}>
                        <Send className="w-4 h-4 mr-1" /> {isUSA ? 'Respond' : 'Responder'}
                      </Button>
                    )}
                  </div>
                  
                  {detailDialog.responses.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">{isUSA ? 'No responses yet' : 'Aún no hay respuestas'}</p>
                  ) : (
                    <div className="space-y-3">
                      {detailDialog.responses.map(resp => (
                        <div key={resp.response_id} className="p-4 bg-white border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                {resp.company_logo ? (
                                  <img src={resp.company_logo} alt="" className="w-10 h-10 rounded-full object-cover" />
                                ) : (
                                  <Building2 className="w-5 h-5 text-blue-600" />
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{resp.company_name || resp.user_name}</p>
                                  {resp.company_id && (
                                    <Link 
                                      to={`/company/${resp.company_id}`}
                                      className="text-xs text-blue-600 hover:underline"
                                      onClick={() => setDetailDialog({...detailDialog, open: false})}
                                    >
                                      {isUSA ? 'View profile' : 'Ver perfil'}
                                    </Link>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500">{new Date(resp.created_at).toLocaleDateString(isUSA ? 'en-US' : 'es-MX')}</p>
                              </div>
                            </div>
                            {resp.company_id && isAuthenticated && (
                              <Link 
                                to={`/dashboard/messages?contact=${resp.company_id}`}
                                onClick={() => setDetailDialog({...detailDialog, open: false})}
                              >
                                <Button size="sm" variant="outline" className="text-xs">
                                  <MessageSquare className="w-3 h-3 mr-1" />
                                  {isUSA ? 'Contact' : 'Contactar'}
                                </Button>
                              </Link>
                            )}
                          </div>
                          <p className="text-slate-700 mb-2">{resp.message}</p>
                          {(resp.price_offer || resp.delivery_time) && (
                            <div className="flex gap-4 text-sm">
                              {resp.price_offer && (
                                <span className="text-emerald-600 font-medium">
                                  ${resp.price_offer.toLocaleString()} MXN
                                </span>
                              )}
                              {resp.delivery_time && (
                                <span className="text-slate-500">
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  {resp.delivery_time}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Response Dialog */}
      <Dialog open={responseDialog.open} onOpenChange={(open) => setResponseDialog({ open, post: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isUSA ? 'Respond to search' : 'Responder a búsqueda'}</DialogTitle>
            <DialogDescription>
              {responseDialog.post?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isUSA ? 'Your message *' : 'Tu mensaje *'}</Label>
              <Textarea 
                placeholder={isUSA ? 'Describe your offer, availability, etc.' : 'Describe tu oferta, disponibilidad, etc.'}
                value={responseForm.message}
                onChange={(e) => setResponseForm({...responseForm, message: e.target.value})}
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isUSA ? 'Price offered (USD)' : 'Precio ofrecido (MXN)'}</Label>
                <Input 
                  type="number"
                  placeholder={isUSA ? 'Ex: 500' : 'Ej: 5000'}
                  value={responseForm.price_offer}
                  onChange={(e) => setResponseForm({...responseForm, price_offer: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>{isUSA ? 'Delivery time' : 'Tiempo de entrega'}</Label>
                <Input 
                  placeholder={isUSA ? 'Ex: 2 weeks' : 'Ej: 2 semanas'}
                  value={responseForm.delivery_time}
                  onChange={(e) => setResponseForm({...responseForm, delivery_time: e.target.value})}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setResponseDialog({ open: false, post: null })}>{isUSA ? 'Cancel' : 'Cancelar'}</Button>
            <Button onClick={handleRespond} disabled={processing} style={{ backgroundColor: '#34B772' }}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Send response' : 'Enviar respuesta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunityBlogPage;
